import LIcon from "@components/LIcon";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { StyleSheet } from "react-native";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import Animated, {
  runOnJS,
  type SharedValue,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";

const QUEUE_DRAG_REORDER_REPORT_DISTANCE_PX = 6;
const REORDER_ACTIVATION_DELAY_MS = 180;
const REORDER_ACTIVATION_DISTANCE_PX = 10;

export const QueueDragHandleSWM = memo(function QueueDragHandleSWM({
  dragItemStartContentY,
  dragOverlayTop,
  dragStartScrollOffset,
  onBegin,
  onEnd,
  onMove,
  onTouchEnd,
  onTouchStart,
}: {
  dragItemStartContentY: SharedValue<number>;
  dragOverlayTop: SharedValue<number>;
  dragStartScrollOffset: SharedValue<number>;
  onBegin: () => void;
  onEnd: () => void;
  onMove: (translationY: number) => void;
  onTouchEnd: () => void;
  onTouchStart: () => void;
}) {
  const scale = useSharedValue(1);
  const hasBegunDrag = useSharedValue(false);
  const hasReportedReorder = useSharedValue(false);
  const lastReportedTranslation = useSharedValue(0);
  const beginTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchActiveRef = useRef(false);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  const clearBeginTimer = useCallback(() => {
    if (beginTimerRef.current !== null) {
      clearTimeout(beginTimerRef.current);
      beginTimerRef.current = null;
    }
  }, []);

  const beginDelayedDrag = useCallback(() => {
    beginTimerRef.current = null;

    if (!touchActiveRef.current || hasBegunDrag.value) {
      return;
    }

    hasBegunDrag.value = true;
    hasReportedReorder.value = false;
    lastReportedTranslation.value = 0;
    scale.set(withTiming(1.08, { duration: 80 }));
    onBegin();
  }, [
    hasBegunDrag,
    hasReportedReorder,
    lastReportedTranslation,
    onBegin,
    scale,
  ]);

  const scheduleBeginDrag = useCallback(() => {
    touchActiveRef.current = true;
    clearBeginTimer();
    beginTimerRef.current = setTimeout(
      beginDelayedDrag,
      REORDER_ACTIVATION_DELAY_MS,
    );
  }, [beginDelayedDrag, clearBeginTimer]);

  const endTouch = useCallback(() => {
    touchActiveRef.current = false;
    clearBeginTimer();
    onTouchEnd();
  }, [clearBeginTimer, onTouchEnd]);

  useEffect(() => clearBeginTimer, [clearBeginTimer]);

  const gesture = useMemo(
    () =>
      Gesture.Pan()
        .manualActivation(true)
        .minDistance(0)
        .shouldCancelWhenOutside(false)
        .hitSlop(12)
        .onTouchesDown((_event, stateManager) => {
          runOnJS(onTouchStart)();
          runOnJS(scheduleBeginDrag)();
          stateManager.activate();
        })
        .onUpdate((event) => {
          if (!hasBegunDrag.value) {
            return;
          }

          dragOverlayTop.value =
            dragItemStartContentY.value +
            event.translationY -
            dragStartScrollOffset.value;

          if (!hasReportedReorder.value) {
            if (Math.abs(event.translationY) < REORDER_ACTIVATION_DISTANCE_PX) {
              return;
            }

            hasReportedReorder.value = true;
            lastReportedTranslation.value = event.translationY;
            runOnJS(onMove)(event.translationY);
            return;
          }

          if (
            Math.abs(event.translationY - lastReportedTranslation.value) >=
            QUEUE_DRAG_REORDER_REPORT_DISTANCE_PX
          ) {
            lastReportedTranslation.value = event.translationY;
            runOnJS(onMove)(event.translationY);
          }
        })
        .onFinalize(() => {
          runOnJS(endTouch)();

          if (!hasBegunDrag.value) {
            return;
          }

          hasBegunDrag.value = false;
          scale.set(withTiming(1, { duration: 80 }));
          runOnJS(onEnd)();
        }),
    [
      dragItemStartContentY,
      dragOverlayTop,
      dragStartScrollOffset,
      endTouch,
      hasBegunDrag,
      hasReportedReorder,
      lastReportedTranslation,
      onEnd,
      onMove,
      onTouchStart,
      scheduleBeginDrag,
      scale,
    ],
  );

  return (
    <GestureDetector gesture={gesture}>
      <Animated.View style={[styles.touchTarget, animatedStyle]}>
        <LIcon
          colors={{ color: "blackTranslucent50" }}
          name="drag_handle"
          size={20}
        />
      </Animated.View>
    </GestureDetector>
  );
});

const styles = StyleSheet.create({
  touchTarget: {
    alignItems: "center",
    height: 44,
    justifyContent: "center",
    width: 40,
  },
});
